// The words of a search carried into the document it found, and how they are
// found in a text (components/news/SearchFinder.tsx; decision 27 of 2/11): pure
// functions, tested on their own.

/**
 * A word of the search: its name on the bar, the forms to find (folded), whether it is a beginning (legg*),
 * and whether it is a proper name (`cased`): then only «Fini» and «FINI» count, not «ai fini».
 */
export type FinderTerm = { label: string; forms: string[]; prefix: boolean; cased?: boolean };

/** The query parameter that carries the words from the results to the document. */
export const FINDER_PARAM = "parole";

/** The words of the search as the parameter of a document's address. */
export function finderParam(terms: FinderTerm[]): string {
  return JSON.stringify(terms.map((t) => ({ l: t.label, f: t.forms, p: t.prefix, ...(t.cased ? { c: true } : {}) })));
}

/** The words back from the address; none when the parameter is missing or not ours. */
export function readFinderParam(raw: string | null): FinderTerm[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((t: unknown) => {
      if (typeof t !== "object" || t === null) return [];
      const { l, f, p, c } = t as { l?: unknown; f?: unknown; p?: unknown; c?: unknown };
      if (typeof l !== "string" || !Array.isArray(f)) return [];
      const forms = f.filter((x): x is string => typeof x === "string" && x.trim() !== "").map(foldText);
      return forms.length > 0 ? [{ label: l, forms, prefix: p === true, ...(c === true ? { cased: true } : {}) }] : [];
    });
  } catch {
    return [];
  }
}

/** Lower case, accents off, one character for one: a position in the folded text is one in the text. */
export function foldText(text: string): string {
  let out = "";
  for (const ch of text) {
    const base = ch.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
    out += base.length === ch.length ? base : ch.toLowerCase().length === ch.length ? ch.toLowerCase() : ch;
  }
  return out;
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The expression of a word: any of its forms, whole (a beginning: then any ending), never inside another word. */
export function termPattern(term: FinderTerm): RegExp {
  const forms = [...term.forms]
    .sort((a, b) => b.length - a.length)
    .map((f) => f.split(/\s+/).map(escape).join("[^\\p{L}\\p{N}]+"));
  const end = term.prefix ? "[\\p{L}\\p{N}]*" : "(?![\\p{L}\\p{N}])";
  return new RegExp(`(?<![\\p{L}\\p{N}])(?:${forms.join("|")})${end}`, "gu");
}

/**
 * The places of the word in the text, as [start, length]. A proper name counts only with a capital first
 * letter, as Loggione's «Nome proprio» (Fini, FINI): the text is searched folded, the capital read in the text.
 */
export function matchesIn(text: string, term: FinderTerm): [number, number][] {
  const out: [number, number][] = [];
  for (const m of foldText(text).matchAll(termPattern(term))) {
    const first = text.charAt(m.index);
    if (term.cased && first === first.toLowerCase()) continue;
    out.push([m.index, m[0].length]);
  }
  return out;
}

/** How many times the text has the word. */
export function countIn(text: string, term: FinderTerm): number {
  return matchesIn(text, term).length;
}
